import React, { forwardRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ error }) => (error ? '#fd5c70' : '#d2d6da')};
  border-radius: 0.375rem;
  background-color: white;
  font-size: 0.875rem;
  color: #344767;
  transition: all 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #1a73e8;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const Label = styled.label`
  position: absolute;
  top: ${({ hasValue }) => (hasValue ? '-0.75rem' : '0.75rem')};
  left: 0.75rem;
  padding: 0 0.25rem;
  color: ${({ error }) => (error ? '#fd5c70' : '#6c757d')};
  font-size: ${({ hasValue }) => (hasValue ? '0.75rem' : '0.875rem')};
  transition: all 0.2s ease-in-out;
  pointer-events: none;

  ${StyledInput}:focus ~ & {
    top: -0.75rem;
    font-size: 0.75rem;
    color: #1a73e8;
  }
`;

const ErrorMessage = styled.span`
  display: block;
  color: #fd5c70;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const Input = forwardRef(
  (
    {
      label,
      error,
      value,
      onChange,
      name,
      type = 'text',
      disabled = false,
      placeholder = '',
      ...rest
    },
    ref
  ) => {
    const hasValue = value !== undefined && value !== '';

    return (
      <InputWrapper>
        <StyledInput
          ref={ref}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
          {...rest}
        />
        {label && (
          <Label hasValue={hasValue} error={error}>
            {label}
          </Label>
        )}
        {error && typeof error === 'string' && <ErrorMessage>{error}</ErrorMessage>}
      </InputWrapper>
    );
  }
);

Input.propTypes = {
  label: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default Input;
